import { gql } from '@apollo/client';
import { Box } from '@mui/material';
import { CUSTOM_RANGE_LABEL } from 'components/common/DatePicker/DateRangePicker';
import { IconLinearNote } from 'components/icons/components/linear/IconLinearNote';
import { SLALoadingState } from 'features/socialListeningAnalytics/components/slaLoadingState';
import { generateDateRangeOptions } from 'features/socialListeningCreatorProfile/utils';
import { SocialMediaListeningFilterByTopicsButton } from 'features/socialMediaListening/components';
import {
  BrandFragmentBrandConnectedCreatorsFragmentDoc,
  BrandMetricsFilters,
  Platform,
  SocialPostCommentsFilters,
  SocialPostCommentSource,
  TopicFragmentSocialMediaListeningFilterByTopicsButtonFragmentDoc,
  useGetBrandOutboundLikeMetricsForSocialMediaListeningAnalyticsOutboundViewQuery,
  useGetBrandOutboundReplyMetricsForSocialMediaListeningAnalyticsOutboundViewQuery,
  useGetBrandOutboundResponseMetricsForSocialMediaListeningAnalyticsOutboundViewQuery,
  useGetBrandsDataForSocialMediaListeningAnalyticsOutboundViewQuery,
} from 'graphql/generated';
import { useLocalStorage } from 'hooks/localStorage/useLocalStorage';
import moment from 'moment';
import {
  SocialMediaListeningAnalyticsOutboundInteractionFilters,
  SocialPostCommentsFiltersWithDateRangeLabel,
} from 'pages/socialMediaListening/analytics/outbound/interaction/components';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { theme } from 'styles/theme';
import * as yup from 'yup';
import {
  BRAND_METRICS_RESPONSE_FRAGMENT_METRICS_CHART,
  LeaderboardSection,
  MetricsChart,
  SocialMediaListeningMostEngagedResponses,
} from './sections';

gql`
  query GetBrandsDataForSocialMediaListeningAnalyticsOutboundView {
    brands {
      id
      name
      ...BrandFragmentBrandConnectedCreators
      topics {
        id
        name
        ...TopicFragmentSocialMediaListeningFilterByTopicsButton
      }
    }
  }
  ${TopicFragmentSocialMediaListeningFilterByTopicsButtonFragmentDoc}
  ${BrandFragmentBrandConnectedCreatorsFragmentDoc}
`;

gql`
  query GetBrandOutboundResponseMetricsForSocialMediaListeningAnalyticsOutboundView(
    $brandId: String!
    $filters: BrandMetricsFilters!
  ) {
    brandOutboundResponseMetrics(filters: $filters, brandId: $brandId) {
      ...BrandMetricsResponseFragmentMetricsChart
    }
  }
  ${BRAND_METRICS_RESPONSE_FRAGMENT_METRICS_CHART}
`;

// eslint-disable-next-line
gql`
  query GetBrandOutboundLikeMetricsForSocialMediaListeningAnalyticsOutboundView(
    $brandId: String!
    $filters: BrandMetricsFilters!
  ) {
    brandOutboundLikeMetrics(filters: $filters, brandId: $brandId) {
      ...BrandMetricsResponseFragmentMetricsChart
    }
  }
  ${BRAND_METRICS_RESPONSE_FRAGMENT_METRICS_CHART}
`;

// eslint-disable-next-line
gql`
  query GetBrandOutboundReplyMetricsForSocialMediaListeningAnalyticsOutboundView(
    $brandId: String!
    $filters: BrandMetricsFilters!
  ) {
    brandOutboundReplyMetrics(filters: $filters, brandId: $brandId) {
      ...BrandMetricsResponseFragmentMetricsChart
    }
  }
  ${BRAND_METRICS_RESPONSE_FRAGMENT_METRICS_CHART}
`;

/**
 * This utility shift the start date to the beginning of the day
 * and the end date to the end of the day.
 */
export const shiftDateRangeForQuery = (dateRange: Date[]) => {
  return [
    moment(dateRange[0]).startOf('day').toDate(),
    moment(dateRange[1]).endOf('day').toDate(),
  ];
};

const SOCIAL_LISTENING_OUTBOUND_FILTERS_LABEL =
  'social-listening-outbound-filters';

// Outbound page render by brand (id):
// Sticky filters bar: Topics selector, Filter by sources, Filter by commenters, Filter by platforms (included post types & creator handles), Filter by date range
// 1. 3 metrics:
// - Brand Comments (brandOutboundResponseMetrics)
// - Total Likes (brandOutboundLikeMetrics)
// - Total Replies (brandOutboundReplyMetrics)
// 2. Most Engaged Responses
// 3. Leaderboard
// So the main page is the 3 metrics
// => use BrandMetricsFilters for filters type
type BrandMetricsFiltersWithLabel = BrandMetricsFilters & {
  dateRangeLabel?: string;
};

export const SocialMediaListeningAnalyticsOutboundView = () => {
  const { brandId = '' } = useParams();
  const { data: brandData, loading: brandLoading } =
    useGetBrandsDataForSocialMediaListeningAnalyticsOutboundViewQuery();

  const brand = brandData?.brands.find((b) => b.id === brandId);
  const defaultDateRange = useMemo(() => {
    return {
      label: '30 days',
      dateRange: [moment().subtract(30, 'days').toDate(), moment().toDate()],
    };
  }, []);

  const keyByBrandId = SOCIAL_LISTENING_OUTBOUND_FILTERS_LABEL + brandId;

  // every filter should be in the local storage
  const {
    value: brandOutboundMetricsFilters_,
    setValue: setBrandOutboundMetricsFilters,
  } = useLocalStorage<Partial<BrandMetricsFiltersWithLabel>>(
    keyByBrandId,
    {
      // Default all platforms
      platforms: [Platform.Tiktok, Platform.Instagram],
      // Default 30 days
      dateRange: defaultDateRange.dateRange,
      dateRangeLabel: defaultDateRange.label,
      topicIds: [],
      // Default both auto and manual sources
      sources: Object.values(SocialPostCommentSource),
      // Default no responder specified => all responders
      responderIds: [],
    },
    yup
      .object()
      .shape({
        platforms: yup
          .array()
          .of(yup.mixed<Platform>().oneOf(Object.values(Platform))),
        dateRange: yup.array().of(yup.date()),
        dateRangeLabel: yup.string().optional(),
        topicIds: yup.array().of(yup.string()),
        sources: yup
          .array()
          .of(
            yup
              .mixed<SocialPostCommentSource>()
              .oneOf(Object.values(SocialPostCommentSource)),
          ),
        responderIds: yup.array().of(yup.string()),
        creatorIds: yup.array().of(yup.string()),
      })
      .noUnknown(),
  );

  // useMemo with JSON.stringify to avoid re-rendering when the filters updated but only the reference changed
  // filters should not contain dateRangeLabel field, as it will crash query
  const brandOutboundMetricsFilters: Partial<BrandMetricsFiltersWithLabel> =
    useMemo(() => {
      // remove dateRangeLabel from the filters
      const { dateRangeLabel, ...rest } = brandOutboundMetricsFilters_;

      // if dateRangeLabel is range like "7 days", "30 days", "3 months", "6 months", "12 months", "this year"
      // we need to parse it to startDate and endDate based on the label and the current date
      if (dateRangeLabel !== CUSTOM_RANGE_LABEL) {
        const options = generateDateRangeOptions();
        const option = options.find((opt) => opt.label === dateRangeLabel);
        if (option) {
          // update the dateRange in the filters
          rest.dateRange = [option.startDate, option.endDate];
        }
      }
      return rest;
    }, [JSON.stringify({ brandOutboundMetricsFilters_ })]); // eslint-disable-line

  const {
    data: brandOutboundResponseMetricsData,
    refetch: refetchBrandOutboundResponseMetrics,
    loading: brandOutboundResponseMetricsLoading,
  } = useGetBrandOutboundResponseMetricsForSocialMediaListeningAnalyticsOutboundViewQuery(
    {
      variables: {
        brandId: brand?.id || '',
        filters: {
          ...brandOutboundMetricsFilters,
          dateRange: brandOutboundMetricsFilters.dateRange
            ? shiftDateRangeForQuery(brandOutboundMetricsFilters.dateRange)
            : undefined,
        },
      },
      skip: !brand?.id,
    },
  );

  useEffect(() => {
    if (brand?.id) {
      refetchBrandOutboundResponseMetrics();
    }
  }, [
    refetchBrandOutboundResponseMetrics,
    brandOutboundMetricsFilters.topicIds,
    brand?.id,
  ]);

  const variables = useMemo(() => {
    return {
      brandId: brand?.id || '',
      filters: {
        ...brandOutboundMetricsFilters,
        dateRange: brandOutboundMetricsFilters.dateRange
          ? shiftDateRangeForQuery(brandOutboundMetricsFilters.dateRange)
          : undefined,
      },
    };
  }, [JSON.stringify({ brandOutboundMetricsFilters, brandId: brand?.id })]); // eslint-disable-line

  const brandOutboundResponseMetrics =
    brandOutboundResponseMetricsData?.brandOutboundResponseMetrics || {
      total: 0,
      chartDataPoints: [],
    };

  // filter for getting most engaged response social posts
  const mostEngagedResponsesFilters: SocialPostCommentsFilters = useMemo(() => {
    return {
      ...brandOutboundMetricsFilters,
      dateRange: {
        startDate: brandOutboundMetricsFilters.dateRange?.[0],
        endDate: brandOutboundMetricsFilters.dateRange?.[1],
      },
      brandIds: [brandId],
    };
  }, [brandOutboundMetricsFilters]);

  const {
    data: brandOutboundLikeMetricsData,
    refetch: refetchBrandOutboundLikeMetrics,
    loading: brandOutboundLikeMetricsLoading,
  } = useGetBrandOutboundLikeMetricsForSocialMediaListeningAnalyticsOutboundViewQuery(
    {
      variables,
      skip: !brand,
    },
  );

  useEffect(() => {
    if (brand?.id) {
      refetchBrandOutboundLikeMetrics();
    }
  }, [
    refetchBrandOutboundLikeMetrics,
    brandOutboundMetricsFilters.topicIds,
    brand?.id,
  ]);

  const brandOutboundLikeMetrics =
    brandOutboundLikeMetricsData?.brandOutboundLikeMetrics || {
      total: 0,
      chartDataPoints: [],
    };

  const {
    data: brandOutboundReplyMetricsData,
    refetch: refetchBrandOutboundReplyMetrics,
    loading: brandOutboundReplyMetricsLoading,
  } = useGetBrandOutboundReplyMetricsForSocialMediaListeningAnalyticsOutboundViewQuery(
    {
      variables: {
        brandId: brand?.id || '',
        filters: {
          ...brandOutboundMetricsFilters,
          dateRange: brandOutboundMetricsFilters.dateRange
            ? shiftDateRangeForQuery(brandOutboundMetricsFilters.dateRange)
            : undefined,
        },
      },
      skip: !brand,
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (brand?.id) {
      refetchBrandOutboundReplyMetrics();
    }
  }, [
    refetchBrandOutboundReplyMetrics,
    brandOutboundMetricsFilters.topicIds,
    brand?.id,
  ]);

  const updateFilters = useCallback(
    (newFilters: Partial<BrandMetricsFiltersWithLabel>) => {
      setBrandOutboundMetricsFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters, ...newFilters };
        if (JSON.stringify(updatedFilters) !== JSON.stringify(prevFilters)) {
          return updatedFilters;
        }
        return prevFilters;
      });
    },
    [setBrandOutboundMetricsFilters],
  );

  if (brandLoading) {
    return (
      <Box
        height="calc(100vh - 200px)"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <SLALoadingState />
      </Box>
    );
  }

  const brandOutboundReplyMetrics =
    brandOutboundReplyMetricsData?.brandOutboundReplyMetrics || {
      total: 0,
      chartDataPoints: [],
    };

  return (
    <Box>
      <Box
        sx={{
          py: 4,
          position: 'sticky',
          top: 64,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 5,
          backgroundColor: theme.colors?.utility[100],
        }}
      >
        <SocialMediaListeningFilterByTopicsButton
          selectedTopicIds={brandOutboundMetricsFilters.topicIds || []}
          onChange={(topicIds) => {
            updateFilters({
              topicIds,
            });
          }}
          componentProps={{
            sx: {
              borderRadius: 25,
              padding: theme.spacing(2, 4),
              ...theme.typography['headline-lg'],
              color: theme.colors?.utility[800],
            },
            startIcon: <IconLinearNote size={24} />,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <SocialMediaListeningAnalyticsOutboundInteractionFilters
            filters={{
              ...mostEngagedResponsesFilters,
              dateRangeLabel: brandOutboundMetricsFilters_.dateRangeLabel,
            }}
            setFilters={(
              newFilters: SocialPostCommentsFiltersWithDateRangeLabel,
            ) => {
              // ensure parse date range to {startDate, endDate} to [Date, Date]
              const dateRangeParsed: Date[] = [
                newFilters.dateRange?.startDate,
                newFilters.dateRange?.endDate,
              ];

              const parsed: BrandMetricsFiltersWithLabel = {
                platforms: newFilters.platforms,
                responderIds: newFilters.responderIds,
                sources: newFilters.sources,
                topicIds: newFilters.topicIds,
                dateRange: dateRangeParsed,
                dateRangeLabel: newFilters.dateRangeLabel,
                creatorIds: newFilters.creatorIds,
              };

              // if dateRangeLabel is range like "7 days", "30 days", "3 months", "6 months", "12 months", "this year"
              // we need to parse it to startDate and endDate based on the label and the current date
              if (
                newFilters?.dateRangeLabel?.length &&
                newFilters.dateRangeLabel !== CUSTOM_RANGE_LABEL
              ) {
                const options = generateDateRangeOptions();
                const option = options.find(
                  (opt) => opt.label === newFilters.dateRangeLabel,
                );
                parsed.dateRange = [option?.startDate, option?.endDate];
              }
              updateFilters(parsed);
            }}
            componentProps={{
              hideFilters: {
                topics: true,
              },
              buttonColors: theme.colors?.utility[300],
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          pb: 16,
        }}
      >
        <MetricsChart
          brandId={brandId}
          metricsData={brandOutboundResponseMetrics}
          labels={{
            title: 'Brand Comments',
            definition: "Total comments you've made on creators' posts.",
            tooltipTitle: 'Interactions',
          }}
          mainColor={theme.colors?.utility[600]}
          highlightColor="rgba(223, 227, 235, 0.50)"
          lastUpdatedAt={moment().subtract(1, 'day').toISOString()}
          filters={brandOutboundMetricsFilters}
          emptyText={
            brandOutboundResponseMetricsLoading ? (
              'Loading...'
            ) : (
              <>
                Oops! No data yet. Time to make some noise! 🎉 <br />
                Start commenting on creators' posts in the Social Listening
                homepage to track your engagement efforts!
              </>
            )
          }
        />
        <MetricsChart
          brandId={brandId}
          metricsData={brandOutboundLikeMetrics}
          labels={{
            title: 'Total Likes',
            definition: "Total likes received on your brand's comments.",
            tooltipTitle: 'Likes',
          }}
          mainColor={theme.colors?.utility['purple-2']}
          highlightColor="rgba(230, 221, 237, 0.50)"
          lastUpdatedAt={moment().subtract(1, 'day').toISOString()}
          filters={brandOutboundMetricsFilters}
          emptyText={
            brandOutboundLikeMetricsLoading
              ? 'Loading...'
              : "Don't worry, brand love will be coming soon. ❤️"
          }
        />
        <MetricsChart
          brandId={brandId}
          metricsData={brandOutboundReplyMetrics}
          labels={{
            title: 'Total Replies',
            definition: 'Number of users who replied to your comments.',
            tooltipTitle: 'Responses',
          }}
          mainColor={theme.colors?.utility['teal-2']}
          highlightColor="rgba(205, 226, 223, 0.50)"
          lastUpdatedAt={moment().subtract(1, 'day').toISOString()}
          filters={brandOutboundMetricsFilters}
          emptyText={
            brandOutboundReplyMetricsLoading
              ? 'Loading...'
              : 'No data. Start commenting to see the magic happen. 🌟'
          }
        />
        {brand && (
          <SocialMediaListeningMostEngagedResponses
            brandId={brand.id}
            filters={mostEngagedResponsesFilters}
          />
        )}
        {brand && (
          <LeaderboardSection
            brandId={brand?.id}
            filters={{
              platforms: brandOutboundMetricsFilters.platforms,
              topicIds: brandOutboundMetricsFilters.topicIds,
              dateRange: {
                startDate: brandOutboundMetricsFilters.dateRange?.[0],
                endDate: brandOutboundMetricsFilters.dateRange?.[1],
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
